/**
 * FlagActionModal Component
 *
 * Modal dialog for taking action on a flagged content item.
 * Allows moderators to resolve or dismiss flags with notes.
 */

import { useState, useCallback, type CSSProperties } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import { FLAG_REASON_LABELS, type ContentFlag, type FlagAction } from '@flash/shared';

/**
 * Props for FlagActionModal component
 */
interface FlagActionModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The flag being acted upon */
  flag: ContentFlag;
  /** The action being taken (resolve or dismiss) */
  action: 'resolve' | 'dismiss';
  /** Called when modal is closed */
  onClose: () => void;
  /** Called when action is submitted */
  onSubmit: (action: FlagAction, notes?: string) => Promise<void>;
}

/**
 * Action options for resolving a flag
 */
const ACTION_OPTIONS: { value: FlagAction; label: string; description: string }[] = [
  {
    value: 'removed',
    label: 'Remove Content',
    description: 'Remove the flagged content from public view',
  },
  {
    value: 'hidden',
    label: 'Hide Content',
    description: 'Hide the content temporarily pending further review',
  },
  {
    value: 'warned',
    label: 'Warn User',
    description: 'Send a warning to the content author',
  },
  {
    value: 'none',
    label: 'No Action',
    description: 'The content does not violate guidelines',
  },
];

/**
 * FlagActionModal component for moderation actions
 */
export const FlagActionModal = ({
  isOpen,
  flag,
  action,
  onClose,
  onSubmit,
}: FlagActionModalProps) => {
  // Form state
  const [selectedAction, setSelectedAction] = useState<FlagAction>('removed');
  const [notes, setNotes] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setSelectedAction('removed');
    setNotes('');
    setError(null);
  }, []);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  }, [isSubmitting, resetForm, onClose]);

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (action === 'resolve' && !selectedAction) {
      setError('Please select an action');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const trimmedNotes = notes.trim();
      // For dismiss action, pass 'none' as the action
      const actionToSubmit = action === 'dismiss' ? ('none' as FlagAction) : selectedAction;
      await onSubmit(actionToSubmit, trimmedNotes || undefined);
      resetForm();
    } catch (submitError) {
      const errorMessage =
        submitError instanceof Error ? submitError.message : 'Failed to process flag';
      console.error('Error processing flag:', submitError);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const isResolve = action === 'resolve';
  const title = isResolve ? 'Take Action on Flag' : 'Dismiss Flag';
  const icon = isResolve ? (
    <WarningIcon style={{ color: 'var(--color-warning)', marginRight: '8px' }} />
  ) : (
    <CancelIcon style={{ color: 'var(--color-text-secondary)', marginRight: '8px' }} />
  );

  return (
    <div
      style={overlayStyle}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="flag-action-modal-title"
    >
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={headerTitleContainerStyle}>
            {icon}
            <h2 id="flag-action-modal-title" style={titleStyle}>
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={closeButtonStyle}
            aria-label="Close"
            disabled={isSubmitting}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Content */}
          <div style={contentStyle}>
            {/* Flag Summary */}
            <div style={summaryContainerStyle}>
              <div style={summaryRowStyle}>
                <span style={summaryLabelStyle}>Content Type:</span>
                <span style={summaryValueStyle}>{flag.contentType}</span>
              </div>
              <div style={summaryRowStyle}>
                <span style={summaryLabelStyle}>Reason:</span>
                <span style={summaryValueStyle}>{FLAG_REASON_LABELS[flag.reason]}</span>
              </div>
              <div style={summaryRowStyle}>
                <span style={summaryLabelStyle}>Reporter:</span>
                <span style={summaryValueStyle}>{flag.reporterDisplayName}</span>
              </div>
              {flag.details && (
                <div style={summaryDetailsStyle}>
                  <span style={summaryLabelStyle}>Details:</span>
                  <p style={summaryDetailsTextStyle}>{flag.details}</p>
                </div>
              )}
            </div>

            {/* Action Options (for resolve action) */}
            {isResolve && (
              <div style={fieldStyle}>
                <label style={labelStyle}>
                  Action <span style={requiredStyle}>*</span>
                </label>
                <div style={radioGroupStyle}>
                  {ACTION_OPTIONS.map((option) => (
                    <label key={option.value} style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="action"
                        value={option.value}
                        checked={selectedAction === option.value}
                        onChange={() => setSelectedAction(option.value)}
                        style={radioInputStyle}
                        disabled={isSubmitting}
                      />
                      <div style={radioContentStyle}>
                        <span style={radioTitleStyle}>{option.label}</span>
                        <span style={radioDescriptionStyle}>{option.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Dismissal info (for dismiss action) */}
            {!isResolve && (
              <div style={dismissInfoStyle}>
                <p style={dismissInfoTextStyle}>
                  Dismissing this flag means you have reviewed it and determined no action is
                  needed. The content will remain as-is.
                </p>
              </div>
            )}

            {/* Notes */}
            <div style={fieldStyle}>
              <label htmlFor="action-notes" style={labelStyle}>
                {isResolve ? 'Moderator Notes' : 'Reason for Dismissal'} (optional)
              </label>
              <textarea
                id="action-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  isResolve
                    ? 'Add any notes about this moderation action...'
                    : 'Explain why this flag is being dismissed...'
                }
                style={textareaStyle}
                rows={3}
                maxLength={500}
                disabled={isSubmitting}
              />
              <div style={charCountStyle}>{notes.length}/500</div>
            </div>

            {/* Error Message */}
            {error && <div style={errorStyle}>{error}</div>}
          </div>

          {/* Actions */}
          <div style={actionsStyle}>
            <button
              type="button"
              onClick={handleClose}
              style={cancelButtonStyle}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...submitButtonStyle,
                ...(isSubmitting ? disabledButtonStyle : {}),
                ...(isResolve ? {} : dismissSubmitStyle),
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                'Processing...'
              ) : isResolve ? (
                <>
                  <CheckCircleIcon fontSize="small" />
                  <span>Submit Resolution</span>
                </>
              ) : (
                <>
                  <CancelIcon fontSize="small" />
                  <span>Dismiss Flag</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Styles
 */
const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px',
  zIndex: 1000,
};

const modalStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  maxWidth: '500px',
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: 'var(--shadow-lg)',
};

const headerStyle: CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid var(--color-border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const headerTitleContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const titleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: 0,
};

const closeButtonStyle: CSSProperties = {
  padding: '8px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '6px',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentStyle: CSSProperties = {
  padding: '20px 24px',
};

const summaryContainerStyle: CSSProperties = {
  marginBottom: '20px',
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
};

const summaryRowStyle: CSSProperties = {
  display: 'flex',
  marginBottom: '8px',
};

const summaryLabelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  minWidth: '100px',
};

const summaryValueStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
};

const summaryDetailsStyle: CSSProperties = {
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: '1px solid var(--color-border)',
};

const summaryDetailsTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  lineHeight: 1.6,
  margin: '8px 0 0 0',
};

const fieldStyle: CSSProperties = {
  marginBottom: '20px',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

const requiredStyle: CSSProperties = {
  color: 'var(--color-error)',
};

const radioGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
};

const radioLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  cursor: 'pointer',
  padding: '12px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
  transition: 'border-color 0.15s',
};

const radioInputStyle: CSSProperties = {
  marginTop: '4px',
  flexShrink: 0,
};

const radioContentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const radioTitleStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const radioDescriptionStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
  marginTop: '2px',
};

const dismissInfoStyle: CSSProperties = {
  marginBottom: '20px',
  padding: '16px',
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
};

const dismissInfoTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.6,
  margin: 0,
};

const textareaStyle: CSSProperties = {
  width: '100%',
  padding: '12px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  resize: 'vertical',
  minHeight: '80px',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

const charCountStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
  textAlign: 'right',
  marginTop: '4px',
};

const errorStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-error)',
  color: 'var(--color-error)',
  fontSize: '0.875rem',
};

const actionsStyle: CSSProperties = {
  padding: '16px 24px',
  borderTop: '1px solid var(--color-border)',
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const cancelButtonStyle: CSSProperties = {
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border-hover)',
  borderRadius: '8px',
  cursor: 'pointer',
};

const submitButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '10px 20px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'white',
  backgroundColor: 'var(--color-primary)',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const dismissSubmitStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-secondary)',
};

const disabledButtonStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted)',
  cursor: 'not-allowed',
};

export default FlagActionModal;
